import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnatomicalSideEnum, ApiResponse, ApiResponseStatusCode, BoneTypeEnum, DeviceEnum, IntegrationRepository, MigratePlan, Plan, PlanRepository, PlanRequest } from '../core';
import { AnatomicalSiteEnum, Attachment, PlanForm } from '../models';


/**
 * This service handles plan data.
 */
@Injectable({ providedIn: 'root' })
export class PlanService {

	private readonly planRepo = inject(PlanRepository);
	private readonly intRepo = inject(IntegrationRepository);

	/**
	* Map plan request from plan form.
	*/
	createPlanRequest(form: PlanForm): PlanRequest {
		return {
			patientGuid: form.patientGuid,
			number: form.planId,
			notes: form.notes ? form.notes : null,
			device: form.device,
			type: form.planType,
			referenceType: form.referenceType,
			isPostOperative: form.postOperative ?? false,
			boneType: form.boneType,
			side: form.side,
			apImageGuid: form.withImages ? form.apImage?.id : undefined,
			ltImageGuid: form.withImages ? form.ltImage?.id : undefined
		};
	}

	/**
	* Map plan form from plan data.
	*/
	createPlanForm(plan: Plan, apImage: Attachment | null, ltImage: Attachment | null, forceWithImages: boolean): PlanForm {
		return {
			patientGuid: plan.patientId,
			planId: plan.number,
			notes: plan.notes ? decodeURI(plan.notes) : null,
			device: plan.device,
			planType: plan.type,
			referenceType: plan.referenceType,
			postOperative: plan.isPostOperative,
			withImages: !!plan.apImageGuid || !!plan.ltImageGuid || forceWithImages,
			anatomicalSite: getAnatomicalSite(plan.side, plan.boneType),
			boneType: plan.boneType,
			side: plan.side,
			apImage: apImage,
			ltImage: ltImage
		};
	}

	/**
	* Map plan model from plan form.
	*/
	updatePlanModel(plan: Plan, planForm: PlanForm): Plan {
		return {
			...plan,
			number: planForm.planId,
			device: planForm.device,
			notes: planForm.notes ? planForm.notes : null,
			type: planForm.planType,
			referenceType: planForm.referenceType,
			isPostOperative: planForm.postOperative,
			boneType: planForm.boneType,
			side: planForm.side,
			apImageGuid: planForm.withImages ? planForm.apImage?.id : undefined,
			ltImageGuid: planForm.withImages ? planForm.ltImage?.id : undefined
		};
	}

	/**
	* Get plan by plan guid.
	* @param {string} planGuid The plan guid.
	*/
	getPlan(planGuid: string): Observable<Plan> {
		return this.planRepo.getPlan(planGuid).pipe(
			map(res => this.handlePlanResponse(res))
		);
	}

	/**
	* Save plan by plan request.
	*/
	savePlan(request: PlanRequest): Observable<Plan> {
		return this.planRepo.savePlan(request).pipe(
			map(res => this.handlePlanResponse(res)),
		);
	}

	/**
	* Save plan by plan form.
	*/
	savePlanForm(planForm: PlanForm): Observable<Plan> {
		const planRequest: PlanRequest = this.createPlanRequest(planForm);
		return this.savePlan(planRequest);
	}

	/**
	* Update plan with plan form data.
	*/
	updatePlan(plan: Plan, planForm: PlanForm): Observable<Plan> {
		const planToUpdate = this.updatePlanModel(plan, planForm);
		return this.planRepo.updatePlan(planToUpdate).pipe(
			map(res => this.handlePlanResponse(res)),
			map(() => planToUpdate)
		);
	}

	/**
	* Delete plan by plan guid.
	*/
	deletePlan(planGuid: string): Observable<any> {
		return this.planRepo.deletePlan(planGuid);
	}

	/**
	 * Migrate plan.
	 */
	migratePlan(planId: string, device?: DeviceEnum | null, notes?: string | null): Observable<void> {
		const migratePlan: MigratePlan = { device: device ?? undefined, note: notes ?? undefined };
		return this.intRepo.migratePlan(planId, migratePlan).pipe(
			map(res => this.handleMigrateResponse(res))
		);
	}

	private handlePlanResponse(response: ApiResponse<Plan>): Plan {
		switch (response.statusCode) {
			case ApiResponseStatusCode.Success: return response.result as Plan;
			case ApiResponseStatusCode.CaseNumberNotUnique: throw new Error('PlanNumberNotUnique');
			default: throw new Error('Generic error');
		}
	}

	private handleMigrateResponse<T>(response: ApiResponse<T>): T {
		switch (response.statusCode) {
			case ApiResponseStatusCode.Success: return response.result as T;
			case ApiResponseStatusCode.UnsupportedModel: throw new Error('UnsupportedModel');
			default: throw new Error('Generic error');
		}
	}
}

/**
* Get anatomical site from anatomical side and bone type.
*/
export function getAnatomicalSite(side: AnatomicalSideEnum | null, boneType: BoneTypeEnum | null): AnatomicalSiteEnum | null {
	if (side == AnatomicalSideEnum.Right) {
		switch (boneType) {
			case BoneTypeEnum.LongLeg: return AnatomicalSiteEnum.RightLongLeg;
			case BoneTypeEnum.Femur: return AnatomicalSiteEnum.RightFemur;
			case BoneTypeEnum.Tibia: return AnatomicalSiteEnum.RightTibia;
			case BoneTypeEnum.Ankle: return AnatomicalSiteEnum.RightAnkle;
			case BoneTypeEnum.Forefoot: return AnatomicalSiteEnum.RightForefoot;
			case BoneTypeEnum.Hindfoot: return AnatomicalSiteEnum.RightHindfoot;
			default: return null;
		}
	}
	if (side == AnatomicalSideEnum.Left) {
		switch (boneType) {
			case BoneTypeEnum.LongLeg: return AnatomicalSiteEnum.LeftLongLeg;
			case BoneTypeEnum.Femur: return AnatomicalSiteEnum.LeftFemur;
			case BoneTypeEnum.Tibia: return AnatomicalSiteEnum.LeftTibia;
			case BoneTypeEnum.Ankle: return AnatomicalSiteEnum.LeftAnkle;
			case BoneTypeEnum.Forefoot: return AnatomicalSiteEnum.LeftForefoot;
			case BoneTypeEnum.Hindfoot: return AnatomicalSiteEnum.LeftHindfoot;
			default: return null;
		}
	}
	return null;
}

/**
* Get anatomical side from anatomical site.
*/
export function getAnatomicalSide(anatomicalSite: AnatomicalSiteEnum): AnatomicalSideEnum | null {
	if (RIGHTS.has(anatomicalSite)) {
		return AnatomicalSideEnum.Right;
	} else if (LEFTS.has(anatomicalSite)) {
		return AnatomicalSideEnum.Left;
	}
	return null;
}

/**
* Get bone type from anatomical site.
*/
export function getBoneType(anatomaicalSite: AnatomicalSiteEnum): BoneTypeEnum | null {
	if (LONGLEGS.has(anatomaicalSite)) {
		return BoneTypeEnum.LongLeg;
	} else if (FEMURS.has(anatomaicalSite)) {
		return BoneTypeEnum.Femur;
	} else if (TIBIAS.has(anatomaicalSite)) {
		return BoneTypeEnum.Tibia;
	} else if (ANKLES.has(anatomaicalSite)) {
		return BoneTypeEnum.Ankle;
	} else if (FOREFOOTS.has(anatomaicalSite)) {
		return BoneTypeEnum.Forefoot;
	} else if (HINDFOOTS.has(anatomaicalSite)) {
		return BoneTypeEnum.Hindfoot;
	}
	return null;
}

const RIGHTS = new Set([
	AnatomicalSiteEnum.RightLongLeg,
	AnatomicalSiteEnum.RightFemur,
	AnatomicalSiteEnum.RightTibia,
	AnatomicalSiteEnum.RightAnkle,
	AnatomicalSiteEnum.RightForefoot,
	AnatomicalSiteEnum.RightHindfoot
]);
const LEFTS = new Set([
	AnatomicalSiteEnum.LeftLongLeg,
	AnatomicalSiteEnum.LeftFemur,
	AnatomicalSiteEnum.LeftTibia,
	AnatomicalSiteEnum.LeftAnkle,
	AnatomicalSiteEnum.LeftForefoot,
	AnatomicalSiteEnum.LeftHindfoot
]);
const LONGLEGS = new Set([AnatomicalSiteEnum.RightLongLeg, AnatomicalSiteEnum.LeftLongLeg]);
const FEMURS = new Set([AnatomicalSiteEnum.RightFemur, AnatomicalSiteEnum.LeftFemur]);
const TIBIAS = new Set([AnatomicalSiteEnum.RightTibia, AnatomicalSiteEnum.LeftTibia]);
const ANKLES = new Set([AnatomicalSiteEnum.RightAnkle, AnatomicalSiteEnum.LeftAnkle]);
const FOREFOOTS = new Set([AnatomicalSiteEnum.RightForefoot, AnatomicalSiteEnum.LeftForefoot]);
const HINDFOOTS = new Set([AnatomicalSiteEnum.RightHindfoot, AnatomicalSiteEnum.LeftHindfoot]);